import React, { useRef, useState, useEffect, useDeferredValue } from 'react';
import { View, StyleSheet, Pressable, Text, Image, TouchableOpacity, Animated, useWindowDimensions, Platform, Share, ActivityIndicator } from 'react-native';
import Svg, { Path, G } from 'react-native-svg';
import { SafeAreaView } from 'react-native-safe-area-context';
// Import SvgCanvas based on platform
import SvgCanvas from '../src/components/SvgCanvas.web';
import ViewShot from 'react-native-view-shot';
import { parseDailySvg, LayerData, getSvgDimensions } from '../src/core/svgParser';
// @ts-ignore
import AsyncStorage from '@react-native-async-storage/async-storage';
import { CameraView, useCameraPermissions } from 'expo-camera';

// Modals & Helpers
import LandingModal from '../src/components/modals/LandingModal';
import MenuModal from '../src/components/modals/MenuModal';
import CompletionModal from '../src/components/modals/CompletionModal';
import RestartModal from '../src/components/modals/RestartModal';
import HowToPlayModal from '../src/components/modals/HowToPlayModal';
import ContinueModal from '../src/components/modals/ContinueModal';
import PackBrowserModal, { mockPacks } from '../src/components/modals/PackBrowserModal';
import SelectPuzzleModal from '../src/components/modals/SelectPuzzleModal';
import CustomModelModal from '../src/components/modals/CustomModelModal';
import WhyPicloModal from '../src/components/modals/WhyPicloModal';
import CameraPermissionModal from '../src/components/modals/CameraPermissionModal';
import UnlockedModal from '../src/components/modals/UnlockedModal';
import CustomPuzzleGreetingModal from '../src/components/modals/CustomPuzzleGreetingModal';
import ExpiredPuzzleModal from '../src/components/modals/ExpiredPuzzleModal';
import TermsOfUseModal from '../src/components/modals/TermsOfUseModal';
import PrivacyPolicyModal from '../src/components/modals/PrivacyPolicyModal';
import { colors, typography } from '../src/styles/theme';
import { generateShareString } from '../src/utils/shareHelper';
// @ts-ignore - Ignore missing module error
import { getMaskedPixels } from '../src/core/pixelSampler';
import { calculateLayerAccuracy } from '../src/core/colorMath';
import { FontAwesome6 } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import AudioManager from '../src/utils/AudioManager';
import { saveImage, loadImage } from '../src/utils/PlatformStorage';
import * as Sentry from '@sentry/react-native';
const RootContainer = Platform.OS === 'web' ? View : SafeAreaView;

const DAILY_PUZZLE_URL = 'https://piclo.art/templates/daily.svg';
// Define the resolution multiplier constant to match the legacy implementation
const CAPTURE_RESOLUTION_MULTIPLIER = 2.5;
const PICLO_PLAY_PRODUCT_ID = 'pri_01ks79bqva52p3cbwd6m18ajn0';

import {
  useFonts,
  JosefinSans_600SemiBold,
  JosefinSans_400Regular
} from '@expo-google-fonts/josefin-sans';
// Fix the import name to match the actual export
import { FrederickatheGreat_400Regular as FrederickaTheGreat_400Regular } from '@expo-google-fonts/fredericka-the-great';
import { useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { Entitlements, CapturedLayer, PuzzleCache, PuzzleState } from '../src/types';

const AnimatedPath = Animated.createAnimatedComponent(Path);

// Inject pure CSS and a JS resize listener to force the web DOM to provide a perfect canvas
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  if (!document.getElementById('piclo-root-styles')) {
    // 1. Inject the Styles
    const style = document.createElement('style');
    style.id = 'piclo-root-styles';
    style.innerHTML = `
      :root {
        --app-height: 100dvh; /* Fallback */
      }
      html, body, #root {
        height: var(--app-height); /* Rely on JS pixel math instead of buggy CSS */
        width: 100vw;
        margin: 0;
        padding: 0;
        background-color: #000;
        overflow: hidden; 
      }
      
      /* THE EXPO MIRROR KILLER */
      video {
        transform: none !important;
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        object-position: center !important;
      }
    `;
    document.head.appendChild(style);

    // 2. Inject the Real-Time Height Calculator
    const appHeight = () => {
      // Force the CSS variable to match the absolute physical pixels of the inner window
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    // Listen for resize and orientation changes
    window.addEventListener('resize', appHeight);
    window.addEventListener('orientationchange', () => {
      // Slight delay for orientation changes to let the browser toolbar settle
      setTimeout(appHeight, 50);
    });

    // Fire immediately on boot
    appHeight();
  }
}

// --- WEB RTC RESOLUTION HIJACK ---
// Intercept Expo's generic camera request and force high-resolution constraints
if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.mediaDevices) {
  const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  navigator.mediaDevices.getUserMedia = async (constraints) => {
    if (constraints && constraints.video) {
      const videoConstraints = typeof constraints.video === 'object' ? constraints.video : {};
      const isPortrait = window.innerHeight > window.innerWidth;
      constraints.video = {
        ...videoConstraints,
        width: { ideal: isPortrait ? 2160 : 4096 },
        height: { ideal: isPortrait ? 4096 : 2160 },
        facingMode: videoConstraints.facingMode || 'environment'
      };
    }
    return originalGetUserMedia(constraints);
  };
}


// Initialize logging
Sentry.init({
  dsn: 'https://3e0e7d1f407aff8922fba64e48022ae0@o4511420665692160.ingest.us.sentry.io/4511420706586624', // You get this when you create a Sentry project

  // Set to 1.0 during testing, but drop to 0.1 for production 
  // so you don't burn your free tier quota on performance traces
  tracesSampleRate: 0.1,

  // Disable these heavy features to protect your web bundle size
  enableAutoSessionTracking: false,
  enableAutoPerformanceTracing: false,
});

// --- WEB CAPTURE UN-FLIPPER ---
// Expo's web engine forcefully mirrors the internal canvas during takePictureAsync.
// This reverses the pixels 1:1 before scoring, ensuring the mask evaluates 
// physical reality without altering the aspect ratio.
const unflipWebCapture = async (photoUri: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Keep the exact original high-res camera dimensions
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(photoUri);

      // Undo Expo's rogue horizontal flip
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      ctx.clearRect(0, 0, canvas.width, canvas.height); // Explicitly clear canvas memory to force garbage collection
      resolve(dataUrl);
    };
    img.onerror = () => resolve(photoUri);
    img.src = photoUri;
  });
};

const getPuzzleNumber = () => {
  // Match webapp epoch: August 31, 2026 at 07:00 UTC (Midnight PDT)
  const epochUTC = Date.UTC(2026, 7, 31, 7, 0, 0);
  const now = new Date();
  const diffTime = now.getTime() - epochUTC;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(1, diffDays + 1);
};

export default function TestScreen() {
  type ModalState = 'landing' | 'menu' | 'completion' | 'restart' | 'howToPlay' | 'continue' | 'packs' | 'puzzles' | 'custom' | 'whyPiclo' | 'unlocked' | 'termsOfUse' | 'privacyPolicy' | 'contact' | 'customGreeting' | 'expiredCustom' | null;

  let [fontsLoaded] = useFonts({
    JosefinSans_600SemiBold,
    JosefinSans_400Regular,
    FrederickaTheGreat_400Regular,
  });

  const params = useLocalSearchParams();
  const router = useRouter();
  const customPuzzleParam = params.custom_puzzle;
  const todayKey = `piclo_daily_${getPuzzleNumber()}`;

  const cameraRef = useRef<any>(null);
  const canvasShotRef = useRef<any>(null);
  // Add refs for export functionality
  const svgRef = useRef<any>(null);
  const maskedCanvasRef = useRef<any>(null);
  const [layers, setLayers] = useState<LayerData[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  // This allows the UI to stay responsive while the heavy SVG processes
  const deferredActiveId = useDeferredValue(activeId);
  const [capturedLayers, setCapturedLayers] = useState<CapturedLayer[]>([]);

  // UI State
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isEasyMode, setIsEasyMode] = useState<boolean>(false);
  const [isTargetVisible, setTargetVisible] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<ModalState>(
    params.custom_puzzle ? null : params.modal === 'packs' ? 'packs' : 'landing'
  );
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [isCompletionAnimating, setIsCompletionAnimating] = useState(false);
  const [svgString, setSvgString] = useState<string | null>(null);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [selectedPackTitle, setSelectedPackTitle] = useState<string>('');
  const [availablePuzzles, setAvailablePuzzles] = useState<any[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const svgOpacity = useRef(new Animated.Value(1)).current;
  const layerAnims = useRef(new Map<string, Animated.Value>());
  const [swatchGridLayout, setSwatchGridLayout] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [firstSwatchLayout, setFirstSwatchLayout] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [tutorialSize, setTutorialSize] = useState<{ width: number; height: number } | null>(null);
  const { width: windowWidth, height: nativeWindowHeight } = useWindowDimensions();
  const [webWindowHeight, setWebWindowHeight] = useState(Platform.OS === 'web' ? window.innerHeight : 0);
  const windowHeight = Platform.OS === 'web' ? webWindowHeight : nativeWindowHeight;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handleResize = () => {
      setWebWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const [svgDimensions, setSvgDimensions] = useState({ width: 800, height: 1067 });
  const modalTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [permission, requestPermission] = useCameraPermissions();

  const STREAK_COUNT_KEY = 'piclo_streak_count';
  const LAST_COMPLETION_DAY_KEY = 'piclo_last_completion_day';
  const PUZZLE_DATA_KEY = '@piclo:puzzleData';

  const puzzleNumber = getPuzzleNumber();
  const [streak, setStreak] = useState(1);
  const [entitlements, setEntitlements] = useState<Entitlements>({
    picloPlay: false,
    unlockedPacks: [],
  });
  const [showUnlockedModal, setShowUnlockedModal] = useState(false);
  const [unlockedPackName, setUnlockedPackName] = useState<string | null>(null);
  const [isUnlockedPicloPlay, setIsUnlockedPicloPlay] = useState(false);
  const [customPuzzleCount, setCustomPuzzleCount] = useState<number>(0);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const vibrate = (pattern: number | number[] = 50) => {
    if (Platform.OS === 'web') {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(pattern as any);
      }
    } else {
      if (Array.isArray(pattern)) {
        if (pattern.length > 1) {
          let cumTime = 0;
          pattern.forEach((dur, idx) => {
            if (idx % 2 === 0) {
              setTimeout(() => {
                void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
              }, cumTime);
            }
            cumTime += dur;
          });
        } else if (pattern.length === 1 && pattern[0] !== 0) {
          void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        }
      } else if (pattern !== 0) {
        void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    }
  };

  const [customModalTitle, setCustomModalTitle] = useState('Create a Custom Puzzle');
  const [isPlayingCustom, setIsPlayingCustom] = useState(false);
  const isPlayingCustomRef = useRef(false);
  const [isCustomLoading, setIsCustomLoading] = useState(false);
  const [currentPuzzleId, setCurrentPuzzleId] = useState<string | null>(null);
  const currentPuzzleIdRef = useRef<string | null>(null);
  const [currentPuzzleTitle, setCurrentPuzzleTitle] = useState<string>('Daily Piclo');
  const [puzzleCache, setPuzzleCache] = useState<PuzzleCache>({});
  const [hasGrantedPermissionOverride, setHasGrantedPermissionOverride] = useState(false);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [hasActivatedCamera, setHasActivatedCamera] = useState(false);
  const [packBrowserBackTarget, setPackBrowserBackTarget] = useState<'menu' | 'completion'>('menu');
  const [howToPlayBackTarget, setHowToPlayBackTarget] = useState<ModalState | null>(null);
  const [customBackTarget, setCustomBackTarget] = useState<ModalState | null>(null);
  const [pendingCustomId, setPendingCustomId] = useState<string | null>(null);

  const transitionModal = (next: ModalState, title?: string) => {
    if (next === 'packs' && (activeModal === 'menu' || activeModal === 'completion')) {
      setPackBrowserBackTarget(activeModal);
    }
    if (next === 'howToPlay') {
      setHowToPlayBackTarget(activeModal);
    }
    if (next === 'custom') {
      setCustomBackTarget(activeModal);
    }
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }

    if (next === 'custom' && title) {
      setCustomModalTitle(entitlements.picloPlay ? 'Create a Custom Puzzle' : title);
    }

    if (next === null) {
      setActiveModal(null);
      return;
    }

    if (activeModal === null || activeModal === next) {
      setActiveModal(next);
      return;
    }

    setActiveModal(null);
    modalTimeoutRef.current = setTimeout(() => {
      setActiveModal(next);
    }, 100);
  };

  const refreshCustomEntitlements = async () => {
    const savedKey = await AsyncStorage.getItem('@piclo:unlock_key');
    if (savedKey) {
      await fetchServerEntitlements(savedKey, false);
    }
  };

  const pollForUnlockKey = async (transactionId: string) => {
    let attempts = 0;
    const maxAttempts = 30;
    const interval = 2000;

    const runPoll = async () => {
      attempts++;
      try {
        const res = await fetch('https://piclo-entitlements.jscottkuehnert.workers.dev/api/poll-transaction', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transactionId })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success && data.unlockKey) {
            await AsyncStorage.setItem('@piclo:unlock_key', data.unlockKey);
            await fetchServerEntitlements(data.unlockKey, false);
            transitionModal(null);
            setIsAuthenticating(false);
            return;
          }
        }
      } catch (err) {
        console.error("Error polling transaction:", err);
      }

      if (attempts >= maxAttempts) {
        setIsAuthenticating(false);
        //alert("Purchase successful! Your unlock link is on its way to your email.");
        return;
      }

      setTimeout(runPoll, interval);
    };

    setTimeout(runPoll, interval);
  };

  const fetchServerEntitlements = async (key: string, isFromMagicLink: boolean) => {
    try {
      const res = await fetch('https://piclo-entitlements.jscottkuehnert.workers.dev/api/my-entitlements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unlockKey: key })
      });

      if (!res.ok) throw new Error('Failed to fetch entitlements from server');

      const data = await res.json();

      if (data.success) {
        // Map Paddle IDs to our internal mockPacks IDs
        const resolvedPacks: string[] = [];
        data.productIds.forEach((id: string) => {
          const match = mockPacks.find(p => p.priceId === id || p.id === id);
          if (match) {
            resolvedPacks.push(match.id);
          } else if (id && !id.startsWith('pro_')) {
            // Fallback for legacy string IDs if they exist in DB
            resolvedPacks.push(id);
          }
        });

        // Read directly from AsyncStorage to bypass React state race conditions
        const localStr = await AsyncStorage.getItem('@piclo:entitlements');
        let localPacks: string[] = [];
        let localPicloPlay = false;
        if (localStr) {
          try {
            const parsed = JSON.parse(localStr);
            localPacks = parsed.unlockedPacks || [];
            localPicloPlay = !!parsed.picloPlay;
          } catch (e) { }
        }

        const mergedPacks = Array.from(new Set([...localPacks, ...resolvedPacks]));
        const updated = {
          picloPlay: localPicloPlay || data.picloPlay,
          unlockedPacks: mergedPacks
        };

        await AsyncStorage.setItem('@piclo:entitlements', JSON.stringify(updated));
        setEntitlements(updated);

        if (typeof data.customPuzzleCount === 'number') {
          setCustomPuzzleCount(data.customPuzzleCount);
          await AsyncStorage.setItem('@piclo:customPuzzleCount', String(data.customPuzzleCount));
        }

        // Trigger the success modal routing if coming from an email
        if (isFromMagicLink) {
          // Find exactly what was added by comparing server state to local state
          const newPacks = resolvedPacks.filter(id => !localPacks.includes(id));
          const newlyUnlockedPicloPlay = data.picloPlay && !localPicloPlay;

          let packNameForModal = null;
          if (newPacks.length > 0) {
            // Prioritize the newly unlocked pack
            const pack = mockPacks.find(p => p.id === newPacks[newPacks.length - 1]);
            if (pack) packNameForModal = pack.title;
          } else if (!data.picloPlay && resolvedPacks.length > 0) {
            // Fallback if we can't find a delta, just grab the latest pack
            const pack = mockPacks.find(p => p.id === resolvedPacks[resolvedPacks.length - 1]);
            if (pack) packNameForModal = pack.title;
          }

          if (packNameForModal) {
            // If they unlocked a specific pack, show THAT pack, even if they have Piclo Play
            setUnlockedPackName(packNameForModal);
            setIsUnlockedPicloPlay(false);
            AsyncStorage.setItem('@piclo:showUnlockedSuccess', JSON.stringify({ packName: packNameForModal })).catch(console.error);
            setShowUnlockedModal(true);
          } else if (newlyUnlockedPicloPlay || data.picloPlay) {
            // Only fall back to the Piclo Play modal if no specific pack was identified
            setIsUnlockedPicloPlay(true);
            setUnlockedPackName(null);
            AsyncStorage.setItem('@piclo:showUnlockedSuccess', JSON.stringify({ isPicloPlay: true })).catch(console.error);
            setShowUnlockedModal(true);
          }
        }
      }
    } catch (error) {
      console.error("Server entitlement sync failed:", error);
    }
  };


  const loadDailyPuzzle = async () => {
    try {
      const urlWithCacheBuster = `${DAILY_PUZZLE_URL}?t=${Date.now()}`;
      const response = await fetch(urlWithCacheBuster);
      if (!response.ok) throw new Error(`Failed to fetch daily SVG. Status: ${response.status}`);
      const fetchedSvgString = await response.text();
      setSvgString(fetchedSvgString);
      const parsedLayers = parseDailySvg(fetchedSvgString);
      setLayers(parsedLayers);
      setSvgDimensions(getSvgDimensions(fetchedSvgString));

      setIsPlayingCustom(false);
      isPlayingCustomRef.current = false;
      setCurrentPuzzleId(todayKey);
      currentPuzzleIdRef.current = todayKey;
      setCurrentPuzzleTitle(`Piclo #${puzzleNumber}`);

      parsedLayers.forEach(layer => {
        if (!layerAnims.current.has(layer.id)) {
          layerAnims.current.set(layer.id, new Animated.Value(1));
        } else {
          layerAnims.current.get(layer.id)?.setValue(1);
        }
      });
      return parsedLayers;
    } catch (err) {
      console.error("Failed to load daily puzzle:", err);
      throw err;
    }
  };

  useEffect(() => {
    const bootstrapAsync = async () => {
      try {
        // --- 1. Load Streak ---
        const storedStreak = await AsyncStorage.getItem(STREAK_COUNT_KEY);
        const lastDay = await AsyncStorage.getItem(LAST_COMPLETION_DAY_KEY);
        if (storedStreak && lastDay) {
          const count = parseInt(storedStreak);
          const lastDayNum = parseInt(lastDay);
          // If last completion was today or yesterday (PST), streak is alive
          if (lastDayNum === puzzleNumber || lastDayNum === puzzleNumber - 1) {
            setStreak(count);
          } else {
            setStreak(1);
          }
        }

        // --- 2. Load Global Cache Dictionary ---
        const savedCacheStr = await AsyncStorage.getItem(PUZZLE_DATA_KEY);
        const currentCache: PuzzleCache = savedCacheStr ? JSON.parse(savedCacheStr) : {};
        setPuzzleCache(currentCache);

        // --- 3. Sync Authoritative Entitlements ---
        const savedKey = await AsyncStorage.getItem('@piclo:unlock_key');
        if (savedKey) {
          console.log("Bootstrap: Found unlock key, syncing authoritative entitlements from server...");
          void fetchServerEntitlements(savedKey, false);
        }

        // --- 4. Load Local Optimistic Entitlements ---
        const savedEntitlementsStr = await AsyncStorage.getItem('@piclo:entitlements');
        if (savedEntitlementsStr) {
          try {
            const savedEntitlements = JSON.parse(savedEntitlementsStr);
            // Safely merge local optimistic state with any active state
            setEntitlements(prev => ({
              picloPlay: prev.picloPlay || !!(savedEntitlements.picloPlay || savedEntitlements.hasPicloPlay),
              unlockedPacks: Array.from(new Set([...prev.unlockedPacks, ...(Array.isArray(savedEntitlements.unlockedPacks) ? savedEntitlements.unlockedPacks : [])]))
            }));
          } catch (e) {
            console.error("Bootstrap: Failed to parse local entitlements", e);
          }
        }

        // --- 4.5. Load Custom Puzzle Count ---
        const savedCount = await AsyncStorage.getItem('@piclo:customPuzzleCount');
        if (savedCount !== null) {
          setCustomPuzzleCount(parseInt(savedCount));
        }

        // --- 5. Load Settings ---
        const savedSettingsStr = await AsyncStorage.getItem('@piclo:settings');
        if (savedSettingsStr) {
          try {
            const savedSettings = JSON.parse(savedSettingsStr);
            if (savedSettings.isEasyMode !== undefined) setIsEasyMode(savedSettings.isEasyMode);
            if (savedSettings.isMuted !== undefined) setIsMuted(savedSettings.isMuted);
          } catch (e) {
            console.error("Bootstrap: Failed to parse settings", e);
          }
        }

        // --- 6. Success Modal Check ---
        const showSuccess = await AsyncStorage.getItem('@piclo:showUnlockedSuccess');
        if (showSuccess) {
          try {
            const successData = JSON.parse(showSuccess);
            if (successData.isPicloPlay) setIsUnlockedPicloPlay(true);
            else if (successData.packName) setUnlockedPackName(successData.packName);
            setShowUnlockedModal(true);
          } catch (e) {
            if (showSuccess === 'true') {
              setIsUnlockedPicloPlay(true);
              setShowUnlockedModal(true);
            }
          }
        }

        // --- 7. Fetch SVG (Daily or Custom) ---
        let parsedLayers: LayerData[] = [];
        const isCustom = !!params.custom_puzzle;
        const customId = isCustom ? (Array.isArray(params.custom_puzzle) ? params.custom_puzzle[0] : params.custom_puzzle) : '';

        if (isCustom) {
          setPendingCustomId(customId);
          console.log("Bootstrap: Custom puzzle parameter detected. Loading custom puzzle...", customId);
          const customBucket = process.env.EXPO_PUBLIC_CUSTOM_BUCKET_URL || process.env.EXPO_PUBLIC_BUCKET_URL;
          const svgUrl = `${customBucket}/${customId}.svg`;
          const response = await fetch(svgUrl);
          if (response.status === 404) {
            // Abort custom load and show expired
            console.log("Bootstrap: Custom puzzle not found (expired).");
            setActiveModal('expiredCustom');
            setIsBootstrapped(true);
            return;
          }
          if (!response.ok) throw new Error(`Failed to fetch custom SVG: ${response.status}`);
          const fetchedSvgString = await response.text();
          setSvgString(fetchedSvgString);
          parsedLayers = parseDailySvg(fetchedSvgString);
          setLayers(parsedLayers);
          setSvgDimensions(getSvgDimensions(fetchedSvgString));

          parsedLayers.forEach(layer => {
            if (!layerAnims.current.has(layer.id)) {
              layerAnims.current.set(layer.id, new Animated.Value(1));
            } else {
              layerAnims.current.get(layer.id)?.setValue(1);
            }
          });
        } else {
          parsedLayers = await loadDailyPuzzle();
        }

        // --- 8. Daily Puzzle Migration Check ---
        const dailyId = todayKey;
        if (!isCustom && !currentCache[dailyId]) {
          const legacyDailyData = await AsyncStorage.getItem(todayKey);
          if (legacyDailyData) {
            try {
              const parsed = JSON.parse(legacyDailyData);
              const legacyLayers = Array.isArray(parsed) ? parsed : (parsed?.layers ?? []);
              const legacySeconds = Array.isArray(parsed) ? 0 : (typeof parsed?.secondsElapsed === 'number' ? parsed.secondsElapsed : 0);

              currentCache[dailyId] = {
                isCompleted: legacyLayers.length === parsedLayers.length && parsedLayers.length > 0,
                images: legacyLayers,
                secondsElapsed: legacySeconds,
                title: `Piclo #${puzzleNumber}`
              };
              setPuzzleCache({ ...currentCache });
              await AsyncStorage.setItem(PUZZLE_DATA_KEY, JSON.stringify(currentCache));
            } catch (e) {
              console.error("Migration: Failed to parse legacy data", e);
            }
          }
        }

        // --- 9. Determine Active Puzzle ---
        const savedActiveId = await AsyncStorage.getItem('@piclo:activePuzzleId');
        const savedActiveType = await AsyncStorage.getItem('@piclo:activePuzzleType');

        let targetId = isCustom ? customId : dailyId;
        let targetType = isCustom ? 'custom' : 'daily';
        let targetTitle = isCustom ? 'Custom Piclo' : `Piclo #${puzzleNumber}`;

        if (!isCustom && savedActiveId && currentCache[savedActiveId]) {
          targetId = savedActiveId;
          targetType = savedActiveType || 'pack';
          targetTitle = currentCache[savedActiveId].title || 'Puzzle';
        }

        setCurrentPuzzleId(targetId);
        currentPuzzleIdRef.current = targetId;
        setCurrentPuzzleTitle(targetTitle);

        if (targetType === 'custom') {
          setIsPlayingCustom(true);
          isPlayingCustomRef.current = true;
        }

        // --- 10. Hydrate Images (Restored Promise Map) ---
        let activePuzzle: PuzzleState | undefined = currentCache[targetId];
        if (activePuzzle && activePuzzle.images) {
          // Safeguard: Check for mismatch if this is the daily puzzle, preventing timezone desync / cache collision
          if (targetId === dailyId && parsedLayers && parsedLayers.length > 0) {
            const cachedImages = activePuzzle.images;
            const parsedIds = parsedLayers.map(l => l.id);
            const mismatch = cachedImages.length > parsedLayers.length ||
              cachedImages.some(layer => !parsedIds.includes(layer.id));

            if (mismatch) {
              console.log("Safeguard mismatch detected. Clearing daily cache entry:", targetId);
              delete currentCache[targetId];
              setPuzzleCache({ ...currentCache });
              await AsyncStorage.setItem(PUZZLE_DATA_KEY, JSON.stringify(currentCache));
              activePuzzle = undefined; // Do not load
            }
          }
        }

        if (activePuzzle && activePuzzle.images) {
          const hydratedLayers = await Promise.all(
            activePuzzle.images.map(async (layer) => {
              let storageUri = layer.uri;
              if (typeof storageUri === 'string' && storageUri.startsWith('data:image/')) {
                storageUri = await saveImage(layer.id, storageUri);
              }
              const displayUri = await loadImage(storageUri);

              let blurredStorageUri = layer.blurredUri;
              let blurredDisplayUri = undefined;
              if (blurredStorageUri) {
                if (typeof blurredStorageUri === 'string' && blurredStorageUri.startsWith('data:image/')) {
                  blurredStorageUri = await saveImage(layer.id + '_blur', blurredStorageUri);
                }
                blurredDisplayUri = await loadImage(blurredStorageUri);
              }

              return { ...layer, uri: storageUri, displayUri: displayUri ?? storageUri, blurredUri: blurredStorageUri, blurredDisplayUri: blurredDisplayUri ?? blurredStorageUri };
            })
          );

          setCapturedLayers(hydratedLayers);
          setSecondsElapsed(activePuzzle.secondsElapsed || 0);

          if (!params.unlock_key) {
            if (params.modal === 'packs') {
              transitionModal('packs');
            } else if (isCustom) {
              if (activePuzzle.isCompleted) transitionModal('completion');
              else transitionModal('continue');
            } else if (activePuzzle.isCompleted) {
              transitionModal('completion');
            } else if (hydratedLayers.length > 0) {
              transitionModal('continue');
            } else {
              transitionModal('landing');
            }
          }
        } else if (!params.unlock_key) {
          if (params.modal === 'packs') {
            transitionModal('packs');
          } else if (isCustom) {
            transitionModal('customGreeting');
          } else {
            transitionModal('landing');
          }
        }

        setIsBootstrapped(true);
      } catch (error) {
        console.error("Critical boot failure:", error);
        setIsBootstrapped(true);
      }
    };

    bootstrapAsync();
  }, [todayKey, params.unlock_key, params.modal]);

  useEffect(() => {
    if (params.modal === 'packs') {
      transitionModal('packs');
    }
  }, [params.modal]);



  useEffect(() => {
    if (params.unlock_key) {
      // 1. Persist the raw unlock_key string
      const extractedKey = Array.isArray(params.unlock_key) ? params.unlock_key[0] : params.unlock_key;
      AsyncStorage.setItem('@piclo:unlock_key', extractedKey)
        .catch(err => console.error("Failed to save unlock key", err));

      console.log("Magic Link: Found unlock_key, fetching authoritative database entitlements from server...");
      void fetchServerEntitlements(extractedKey, true);

      // 2. Clean up the URL parameter
      setTimeout(() => {
        if (router.replace) {
          router.replace('/');
        }
      }, 500);
    }
  }, [params.unlock_key]);

  useEffect(() => {
    if (isBootstrapped && currentPuzzleIdRef.current && layers.length > 0) {
      const id = currentPuzzleIdRef.current;

      // Clean layers to avoid persisting transient UI state like displayUri
      const cleanLayers = capturedLayers.map(({ displayUri, blurredDisplayUri, ...rest }) => rest);

      setPuzzleCache(prev => {
        const isComp = capturedLayers.length === layers.length && layers.length > 0;
        const newState: PuzzleState = {
          isCompleted: isComp,
          images: cleanLayers,
          secondsElapsed: secondsElapsed,
          title: currentPuzzleTitle
        };
        const updated = { ...prev, [id]: newState };

        // Save to AsyncStorage
        AsyncStorage.setItem(PUZZLE_DATA_KEY, JSON.stringify(updated))
          .catch(err => console.error("Failed to save puzzle cache:", err));

        return updated;
      });
    }
  }, [capturedLayers, secondsElapsed, isBootstrapped, layers, currentPuzzleTitle]);

  useEffect(() => {
    if (isBootstrapped) {
      AsyncStorage.getItem('@piclo:settings').then((str) => {
        const settings = str ? JSON.parse(str) : {};
        let changed = false;
        if (settings.isEasyMode !== isEasyMode) {
          settings.isEasyMode = isEasyMode;
          changed = true;
        }
        if (settings.isMuted !== isMuted) {
          settings.isMuted = isMuted;
          changed = true;
        }
        if (changed) {
          AsyncStorage.setItem('@piclo:settings', JSON.stringify(settings));
        }
      }).catch(() => { });
    }
  }, [isEasyMode, isMuted, isBootstrapped]);

  // Set hasActivatedCamera to true the first time the user selects a layer
  useEffect(() => {
    if (activeId !== null && !hasActivatedCamera) {
      setHasActivatedCamera(true);
    }
  }, [activeId, hasActivatedCamera]);

  const mediaPromiseRef = useRef<Promise<any> | null>(null);

  // Request camera permission only when a layer is selected and it is undetermined
  useEffect(() => {
    if (activeId !== null && !permission?.granted && !hasGrantedPermissionOverride && permission?.status === 'undetermined' && !isRequestingPermission) {
      if (!mediaPromiseRef.current) {
        setIsRequestingPermission(true);
        if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.mediaDevices) {
          mediaPromiseRef.current = navigator.mediaDevices.getUserMedia({ video: true });
          mediaPromiseRef.current
            .then(() => {
              setIsRequestingPermission(false);
              setHasGrantedPermissionOverride(true);
            })
            .catch((err) => {
              console.error("Camera access denied:", err);
              setIsRequestingPermission(false);
              mediaPromiseRef.current = null;
            });
        } else {
          mediaPromiseRef.current = requestPermission();
          mediaPromiseRef.current
            .then((res) => {
              setIsRequestingPermission(false);
              if (res?.granted) setHasGrantedPermissionOverride(true);
            })
            .catch((err) => {
              console.error("Native camera permission error:", err);
              setIsRequestingPermission(false);
              mediaPromiseRef.current = null;
            });
        }
      }
    }
  }, [activeId, permission, hasGrantedPermissionOverride, isRequestingPermission]);



  useEffect(() => {
    if (!isBootstrapped) return;
    if (activeModal !== 'landing') {
      setIsTimerActive(true);
    } else {
      setIsTimerActive(false);
    }
  }, [isBootstrapped, activeModal]);

  useEffect(() => {
    AudioManager.setMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const scriptId = 'paddle-v2-script';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
        script.async = true;
        script.onload = () => {
          if ((window as any).Paddle) {
            (window as any).Paddle.Initialize({
              token: 'live_a4d3f97e18e8e19fac33137843b',
              eventCallback: function (event: any) {
                if (event.name === 'checkout.completed') {
                  const items = event.data?.items || [];
                  const transactionId = event.data?.transaction_id || event.data?.transaction?.id || event.data?.id;
                  window.dispatchEvent(new CustomEvent('PADDLE_PURCHASE_COMPLETE', {
                    detail: { items, transactionId }
                  }));
                }
              }
            });
          }
        };
        document.head.appendChild(script);
      }
    }
  }, []);

  useEffect(() => {
    const handlePurchaseComplete = async (event: any) => {
      const items = event.detail?.items || [];
      const transactionId = event.detail?.transactionId;
      console.log("Paddle Event: Received PADDLE_PURCHASE_COMPLETE with items:", items, "transactionId:", transactionId);

      if (transactionId) {
        setIsAuthenticating(true);
        void pollForUnlockKey(transactionId);
      }

      let isPicloPlay = false;
      const newPacks: string[] = [];

      items.forEach((item: any) => {
        // Fix 2: Flexible Extraction - check both product and price IDs in various possible locations
        const productId = item.product?.id || item.product_id;
        const priceId = item.price?.id || item.price_id || item.priceId || item.product?.id; // fallback

        console.log("Paddle Event: Processing item - Product ID:", productId, "Price ID:", priceId);

        // Fix 3: Updated Evaluation Logic - check against Piclo Play Product ID
        if (productId === PICLO_PLAY_PRODUCT_ID || priceId === 'pri_01krdg1jtbc5jy52hsea16x6kj') {
          isPicloPlay = true;
          console.log("Paddle Event: Piclo Play unlock recognized via", productId ? "Product" : "Price", "ID");
        } else if (priceId || productId) {
          // Evaluate against mockPacks to find the correct pack ID
          const idToMatch = priceId || productId;
          const matchedPack = mockPacks.find(p => p.priceId === idToMatch);

          if (matchedPack) {
            console.log("Paddle Event: Matched pack found:", matchedPack.id);
            newPacks.push(matchedPack.id);
          } else if (idToMatch && idToMatch.startsWith('pri_pack_')) {
            // Fallback for legacy price pattern
            const packId = idToMatch.replace('pri_pack_', '');
            console.log("Paddle Event: Legacy pack price pattern matched:", packId);
            newPacks.push(packId);
          } else if (idToMatch && idToMatch.startsWith('pro_pack_')) {
            // Fallback for product pattern
            const packId = idToMatch.replace('pro_pack_', '');
            console.log("Paddle Event: Pack product pattern matched:", packId);
            newPacks.push(packId);
          }
        }
      });

      if (isPicloPlay || newPacks.length > 0) {
        setEntitlements(prev => {
          const mergedPacks = Array.from(new Set([...prev.unlockedPacks, ...newPacks]));
          const updatedPicloPlay = prev.picloPlay || isPicloPlay;
          const updated = { picloPlay: updatedPicloPlay, unlockedPacks: mergedPacks };

          console.log("Paddle Event: Persisting updated entitlements to AsyncStorage:", updated);
          // Fix 2: Ensure proper stringification
          AsyncStorage.setItem('@piclo:entitlements', JSON.stringify(updated))
            .catch(err => console.error("Paddle Event: Failed to save entitlement", err));

          return updated;
        });

        if (isPicloPlay) {
          setIsUnlockedPicloPlay(true);
        } else if (newPacks.length > 0) {
          const firstPack = mockPacks.find(p => p.id === newPacks[0]);
          if (firstPack) setUnlockedPackName(firstPack.title);
        }

        setShowUnlockedModal(true);
        const successData = isPicloPlay ? { isPicloPlay: true } : { packName: mockPacks.find(p => p.id === newPacks[0])?.title };
        AsyncStorage.setItem('@piclo:showUnlockedSuccess', JSON.stringify(successData))
          .catch(err => console.error("Paddle Event: Failed to save success flag", err));
      } else {
        console.warn("Paddle Event: No recognizable entitlements found in payload.");
      }
    };

    if (Platform.OS === 'web') {
      window.addEventListener('PADDLE_PURCHASE_COMPLETE', handlePurchaseComplete);
      return () => {
        window.removeEventListener('PADDLE_PURCHASE_COMPLETE', handlePurchaseComplete);
      };
    }
  }, []);

  const uniqueLayers = layers.filter(
    (layer, index, self) => index === self.findIndex((l) => l.id === layer.id)
  );

  const capturedIds = capturedLayers.map(c => c.id);

  const averageScore = capturedLayers.length > 0
    ? Math.round(capturedLayers.reduce((sum, layer) => sum + (layer.score || 0), 0) / capturedLayers.length)
    : 0;

  const isCompleted = uniqueLayers.length > 0 && capturedLayers.length === uniqueLayers.length;

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const timeString = formatTime(secondsElapsed);
  const shareText = generateShareString(puzzleNumber, averageScore, timeString, uniqueLayers.map((layer) => {
    const match = capturedLayers.find((capture) => capture.id === layer.id);
    return Math.round(match?.score ?? 0);
  }));

  useEffect(() => {
    if (!isTimerActive || isCompleted || activeModal !== null) return;
    const interval = setInterval(() => {
      setSecondsElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isTimerActive, isCompleted, activeModal]);

  const tutorialAnchor = swatchGridLayout && firstSwatchLayout
    ? {
      x: swatchGridLayout.x + firstSwatchLayout.x,
      y: swatchGridLayout.y + firstSwatchLayout.y,
      width: firstSwatchLayout.width,
      height: firstSwatchLayout.height
    }
    : null;

  const handleLayerToggle = (id: string, fromSwatchInteraction = true) => {
    if (isCapturing) return;
    if (isCompletionAnimating) return;

    if (!isTimerActive && !isCompleted) {
      setIsTimerActive(true);
    }

    setActiveId((prevId) => {
      const nextId = prevId === id ? null : id;
      if (nextId) {
        setTargetVisible(false);
        setShowTooltip(false); // Hide the tutorial tooltip when any layer is selected
        if (fromSwatchInteraction) {
          void AudioManager.playRandom(AudioManager.swatchSounds, 0.1, 0, 0);
        }
      }
      return nextId;
    });
  };

  const triggerIntroAnimation = (layersToAnimate = layers) => {
    if (!layersToAnimate || layersToAnimate.length === 0) return;

    // Start with all layers hidden
    layersToAnimate.forEach(l => {
      let animVal = layerAnims.current.get(l.id);
      if (!animVal) {
        animVal = new Animated.Value(0);
        layerAnims.current.set(l.id, animVal);
      } else {
        animVal.setValue(0);
      }
    });
    svgOpacity.setValue(1);

    const sorted = [...layersToAnimate].sort((a, b) => {
      const numA = parseInt(a.id.replace(/[^0-9]/g, ''));
      const numB = parseInt(b.id.replace(/[^0-9]/g, ''));
      return numA - numB;
    });

    const targetDuration = 1200;
    const staggerDelay = Math.max(2, targetDuration / sorted.length);

    const animations = sorted.map((layer, index) => {
      const animVal = layerAnims.current.get(layer.id)!;
      return Animated.sequence([
        Animated.delay(index * staggerDelay),
        Animated.timing(animVal, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true
        })
      ]);
    });

    // sorted.forEach((_, index) => {
    //   setTimeout(() => vibrate(15), index * staggerDelay);
    // });

    Animated.parallel(animations).start();
  };

  const captureActive = async () => {
    setIsCapturing(true);
    try {
      if (!activeId) {
        setShowTooltip(true);
        return;
      }

      if (!cameraRef.current) return;

      void AudioManager.playRandom(AudioManager.cameraSounds, 0.1, 0, 70);
      vibrate([50]);

      const photo = await cameraRef.current.takePictureAsync();
      if (!photo) return;

      const layer = layers.find(l => l.id === activeId);
      if (!layer) return;

      // --- THE FIX: Undo Expo's internal canvas mirror before scoring ---
      let processedUri: string | null = photo.uri;
      if (Platform.OS === 'web') {
        processedUri = await unflipWebCapture(photo.uri);
      }

      // Calculate visible viewport based on object-fit: cover scaling
      const scale = Math.max(windowWidth / svgDimensions.width, windowHeight / svgDimensions.height);
      const visibleSvgWidth = windowWidth / scale;
      const visibleSvgHeight = windowHeight / scale;
      const visibleRect = {
        minX: (svgDimensions.width - visibleSvgWidth) / 2,
        maxX: svgDimensions.width - (svgDimensions.width - visibleSvgWidth) / 2,
        minY: (svgDimensions.height - visibleSvgHeight) / 2,
        maxY: svgDimensions.height - (svgDimensions.height - visibleSvgHeight) / 2,
      };

      // @ts-ignore
      // Calculate the ratio between the user's physical screen and the SVG coordinate system
      const screenScale = Math.max(windowWidth / svgDimensions.width, windowHeight / svgDimensions.height);

      // Factor in Retina/High-DPI displays (usually 2 or 3 on mobile/macs)
      const pixelRatio = window.devicePixelRatio || 1;

      // This is the exact multiplier needed to make the mask 1:1 with physical screen pixels
      const dynamicMultiplier = screenScale * pixelRatio;
      let { pixels, base64, blurredBase64, pixelWidth, pixelHeight } = await getMaskedPixels(processedUri, layer.d, svgDimensions.width, svgDimensions.height, visibleRect, dynamicMultiplier);

      // Scale the visibleRect from SVG coordinate space to the actual pixel array coordinate space
      const scaledVisibleRect = {
        minX: (visibleRect.minX / svgDimensions.width) * pixelWidth,
        maxX: (visibleRect.maxX / svgDimensions.width) * pixelWidth,
        minY: (visibleRect.minY / svgDimensions.height) * pixelHeight,
        maxY: (visibleRect.maxY / svgDimensions.height) * pixelHeight,
      };

      const score = calculateLayerAccuracy(pixels, layer.targetHex, scaledVisibleRect, pixelWidth);
      const storedUri = await saveImage(activeId, base64);
      const displayUri = Platform.OS === 'web' ? base64 : await loadImage(storedUri);

      let blurredDisplayUri = undefined;
      let storedBlurredUri = undefined;
      if (blurredBase64) {
        storedBlurredUri = await saveImage(activeId + '_blur', blurredBase64);
        blurredDisplayUri = Platform.OS === 'web' ? blurredBase64 : await loadImage(storedBlurredUri);
      }

      const newCapturedLayers = [
        ...capturedLayers.filter(c => c.id !== activeId),
        { id: activeId, uri: storedUri, displayUri: displayUri ?? storedUri, blurredUri: storedBlurredUri, blurredDisplayUri: blurredDisplayUri, score: score }
      ];

      setCapturedLayers(newCapturedLayers);

      // THE FIX: Give Firefox 25ms to decode and paint the massive Base64 image 
      // into the DOM *before* we hide the live camera feed. 
      setTimeout(() => {
        setActiveId(null);

        if (newCapturedLayers.length === uniqueLayers.length && uniqueLayers.length > 0) {
          setTimeout(() => triggerCompletionReveal(), 1000);
        }
      }, 25);

      // Explicitly set base64 string variables to null to help garbage collection
      base64 = null;
      blurredBase64 = null;
      processedUri = null;
    } catch (error) {
      console.error("Capture processing failed:", error);
    } finally {
      setIsCapturing(false);
    }
  };

  const triggerCompletionReveal = () => {
    setIsCompletionAnimating(true);
    void AudioManager.playRandom(AudioManager.completeSounds, 0.2, 0.25, 0);

    const sorted = [...layers].sort((a, b) => {
      const numA = parseInt(a.id.replace(/[^0-9]/g, ''));
      const numB = parseInt(b.id.replace(/[^0-9]/g, ''));
      return numA - numB;
    });

    // Start with all target layers hidden (opacity 0)
    sorted.forEach(l => layerAnims.current.get(l.id)?.setValue(0));

    const targetDuration = 2000;
    const staggerDelay = Math.max(3, targetDuration / sorted.length);
    const targetColorsHangTime = 3000;

    // Phase 1: Target colors sweep IN over captures
    const fadeInAnims = sorted.map((layer, index) => {
      return Animated.sequence([
        Animated.delay(index * staggerDelay),
        Animated.timing(layerAnims.current.get(layer.id)!, {
          toValue: 1,
          duration: 400,
          useNativeDriver: true
        })
      ]);
    });

    // Phase 2: Target colors sweep OUT to reveal captures
    const fadeOutAnims = sorted.map((layer, index) => {
      return Animated.sequence([
        Animated.delay(sorted.length * staggerDelay + (index * staggerDelay / 2) + targetColorsHangTime),
        Animated.timing(layerAnims.current.get(layer.id)!, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true
        })
      ]);
    });

    // Start Phase 1
    Animated.parallel(fadeInAnims).start();

    setTimeout(() => {
      vibrate([100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 100, 50, 1000]);
    }, 800);

    // Start Phase 2
    Animated.parallel(fadeOutAnims).start(() => {
      setTimeout(async () => {
        // --- STREAK LOGIC ---
        // Only increment streak for the daily puzzle
        if (currentPuzzleIdRef.current === todayKey) {
          const lastDay = await AsyncStorage.getItem(LAST_COMPLETION_DAY_KEY);
          const lastDayNum = lastDay ? parseInt(lastDay) : 0;
          const storedStreak = await AsyncStorage.getItem(STREAK_COUNT_KEY);
          const currentStreakCount = storedStreak ? parseInt(storedStreak) : 0;

          if (puzzleNumber > lastDayNum) {
            // If last was specifically the previous puzzle day, increment. Otherwise restart at 1.
            const newStreak = lastDayNum === puzzleNumber - 1 ? currentStreakCount + 1 : 1;
            setStreak(newStreak);
            await AsyncStorage.setItem(STREAK_COUNT_KEY, newStreak.toString());
            await AsyncStorage.setItem(LAST_COMPLETION_DAY_KEY, puzzleNumber.toString());
          }
        }

        transitionModal('completion');
        setIsTimerActive(false);
        setIsCompletionAnimating(false);
        // Reset ALL layer opacities to 1 so the "eye" button works. 
        // We do this AFTER setIsCompletionAnimating(false) to avoid a "flash" 
        // of target colors.
        layerAnims.current.forEach(anim => anim.setValue(1));
      }, 500);
    });
  };

  // Function to prepare the masked canvas with all captured layers
  const prepareMaskedCanvas = async (containerWidth: number, containerHeight: number) => {
    if (Platform.OS !== 'web' || !maskedCanvasRef.current) return;

    const canvas = maskedCanvasRef.current;

    // --- THE FIX: Dynamic Sizing (Cover logic) ---
    const svgWidth = svgDimensions.width;
    const svgHeight = svgDimensions.height;
    const svgAspectRatio = svgWidth / svgHeight;

    let canvasWidth = containerHeight * svgAspectRatio;
    let canvasHeight = containerHeight;

    if (canvasWidth < containerWidth) {
      canvasWidth = containerWidth;
      canvasHeight = canvasWidth / svgAspectRatio;
    }

    const multiplier = CAPTURE_RESOLUTION_MULTIPLIER;
    canvas.width = canvasWidth * multiplier;
    canvas.height = canvasHeight * multiplier;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear and fill background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load and draw all captured layers
    const drawPromises = capturedLayers.map(layer => {
      if (!layer.displayUri) return Promise.resolve();

      return new Promise<void>((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve();
        };
        img.onerror = () => {
          console.warn(`Failed to load layer image: ${layer.id}`);
          resolve(); // Resolve anyway to not block export
        };
        img.src = layer.displayUri!;
      });
    });

    await Promise.all(drawPromises);
  };

  const handleExportArt = async () => {
    // For React Native platforms, use the ViewShot component
    if (Platform.OS !== 'web') {
      if (!canvasShotRef.current) return;
      try {
        const uri = await canvasShotRef.current.capture?.();
        if (!uri) return;
        await Share.share({ url: uri });
      } catch (error) {
        console.error('Failed to export artwork:', error);
      }
      return;
    }

    // For web platform, use our dedicated export utility
    try {
      // Get the container dimensions for proper cropping
      const containerWidth = canvasShotRef.current?.clientWidth || window.innerWidth;
      const containerHeight = canvasShotRef.current?.clientHeight || window.innerHeight;

      // Prepare the masked canvas - NOW ASYNC
      await prepareMaskedCanvas(containerWidth, containerHeight);

      // Import the export utility dynamically to avoid issues on native platforms
      const { exportArt } = await import('../src/utils/exportArt');

      // Call the export utility with the necessary parameters
      await exportArt({
        svgElement: svgRef.current,
        maskedCanvas: maskedCanvasRef.current,
        containerWidth,
        containerHeight,
        isTargetVisible
      });
    } catch (error) {
      console.error('Failed to export artwork:', error);
    }
  };

  const restartPuzzle = () => {
    setCapturedLayers([]);
    setActiveId(null);
    setTargetVisible(false);
    setSecondsElapsed(0);

    // Set layer animations to 0 synchronously to avoid flash
    layers.forEach(layer => {
      layerAnims.current.get(layer.id)?.setValue(0);
    });

    transitionModal(null);
    void AudioManager.playRandom(AudioManager.introSounds, 0.2, 0, 0);

    // Trigger intro animation sweep
    setTimeout(() => triggerIntroAnimation(), 100);
  };

  const startFreshPuzzle = () => {
    // 1. Play Intro Audio & Haptics
    void AudioManager.playRandom(AudioManager.introSounds, 0.2, 0, 0);
    // vibrate(50);

    // 2. Clear all gameplay state
    setCapturedLayers([]);
    setActiveId(null);
    setTargetVisible(false);
    svgOpacity.setValue(1);
    setSecondsElapsed(0);
    setIsTimerActive(true);

    // Set layer animations to 0 synchronously to avoid flash
    layers.forEach(layer => {
      layerAnims.current.get(layer.id)?.setValue(0);
    });

    // 3. Close all possible modals
    setActiveModal(null);

    // 4. Trigger intro animation sweep
    setTimeout(() => triggerIntroAnimation(), 100);
  };

  const handlePlayCustomPuzzle = async (svgUrl: string, isCustom: boolean, puzzleId?: string) => {
    try {
      setIsCustomLoading(true);
      setIsPlayingCustom(isCustom);
      isPlayingCustomRef.current = isCustom;

      const actualId = puzzleId || todayKey;
      setCurrentPuzzleId(actualId);
      currentPuzzleIdRef.current = actualId;

      // Determine Title
      let title = `Piclo #${puzzleNumber}`;
      if (isCustom) {
        title = "Custom Piclo";
      } else if (puzzleId && puzzleId !== todayKey) {
        // Format pack title like "Animal Puzzle 5"
        const parts = puzzleId.split(/[-_]/);
        if (parts.length >= 2) {
          let prefix = parts[0].replace(/\s\d+$/, '');
          if (prefix.toLowerCase().endsWith('s') && prefix.length > 3) prefix = prefix.slice(0, -1);
          const formattedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1);
          const numMatch = puzzleId.match(/(\d+)$/);
          title = `${formattedPrefix} Puzzle ${numMatch ? numMatch[1] : ''}`;
        } else {
          title = puzzleId.charAt(0).toUpperCase() + puzzleId.slice(1);
        }
      }
      setCurrentPuzzleTitle(title);

      // 1. Play Intro Audio
      void AudioManager.playRandom(AudioManager.introSounds, 0.2, 0, 0);
      // vibrate(50);

      // 2. Fetch SVG content
      const response = await fetch(svgUrl);
      if (response.status === 404) {
        // Abort boot sequence, show expired modal, and clear URL params
        setActiveModal('expiredCustom');
        return;
      }
      if (!response.ok) throw new Error(`Failed to fetch custom SVG: ${response.status}`);
      const svgContent = await response.text();

      // 3. Set and parse SVG
      setSvgString(svgContent);
      const parsedLayers = parseDailySvg(svgContent);
      setLayers(parsedLayers);
      setSvgDimensions(getSvgDimensions(svgContent));

      // 4. Initialize game state from cache or fresh
      const cached = puzzleCache[actualId];
      if (cached && cached.images.length > 0) {
        const hydratedLayers = await Promise.all(
          cached.images.map(async (layer) => {
            const displayUri = await loadImage(layer.uri);
            let blurredDisplayUri = undefined;
            if (layer.blurredUri) {
              blurredDisplayUri = await loadImage(layer.blurredUri);
            }
            return {
              ...layer,
              displayUri: displayUri ?? layer.uri,
              blurredDisplayUri: blurredDisplayUri ?? layer.blurredUri
            };
          })
        );
        setCapturedLayers(hydratedLayers);
        setSecondsElapsed(cached.secondsElapsed);
      } else {
        setCapturedLayers([]);
        setSecondsElapsed(0);
      }

      setActiveId(null);
      setTargetVisible(false);
      setIsTimerActive(true);

      // 5. Initialize animations to 0 to prevent flashing
      parsedLayers.forEach(layer => {
        if (!layerAnims.current.has(layer.id)) {
          layerAnims.current.set(layer.id, new Animated.Value(0));
        } else {
          layerAnims.current.get(layer.id)?.setValue(0);
        }
      });

      // 6. Close modal
      transitionModal(null);

      // 7. Trigger intro animation
      setTimeout(() => triggerIntroAnimation(parsedLayers), 200);
    } catch (err) {
      console.error('Failed to play custom puzzle:', err);
    } finally {
      setIsCustomLoading(false);
    }
  };

  const tooltipMaxWidth = 200;
  const tooltipEdgePadding = 8;
  const tooltipArrowWidth = 12;
  const tooltipArrowHeight = 6;
  const anchorCenterX = tutorialAnchor ? tutorialAnchor.x + tutorialAnchor.width / 2 : 0;
  const measuredTooltipWidth = tutorialSize?.width ?? tooltipMaxWidth;
  const tutorialTooltipWidth = Math.min(measuredTooltipWidth, windowWidth - tooltipEdgePadding * 2);
  const tooltipHeight = tutorialSize?.height ?? 0;
  let tutorialTooltipLeft = anchorCenterX - tutorialTooltipWidth / 2;
  tutorialTooltipLeft = Math.max(
    tooltipEdgePadding,
    Math.min(tutorialTooltipLeft, windowWidth - tutorialTooltipWidth - tooltipEdgePadding)
  );
  const rawTooltipTop = tutorialAnchor
    ? tutorialAnchor.y - tooltipHeight - tooltipArrowHeight - 2
    : 0;
  const tutorialTooltipTop = Math.max(-25, rawTooltipTop);
  const tutorialTooltipArrowLeft = Math.max(
    tooltipEdgePadding,
    Math.min(anchorCenterX - tutorialTooltipLeft - tooltipArrowWidth / 2, tutorialTooltipWidth - tooltipArrowWidth - tooltipEdgePadding)
  );

  const shouldMountCamera = isBootstrapped && activeModal !== 'landing' && (hasActivatedCamera || permission?.granted || hasGrantedPermissionOverride);
  const showPermissionModal = shouldMountCamera && activeId !== null && permission && permission.status === 'denied' && !hasGrantedPermissionOverride && !isRequestingPermission;

  const handleStartGameplay = (callback: () => void) => {
    vibrate(0); // Prime the haptic engine on the first interaction
    // Dismiss the landing modal immediately 
    callback();

    // Trigger the permission request shortly after so the UI doesn't freeze
    if (!permission?.granted && !hasGrantedPermissionOverride && permission?.status === 'undetermined') {
      setIsRequestingPermission(true);
      setTimeout(() => {
        requestPermission().then((res) => {
          setIsRequestingPermission(false);
          if (res?.granted) setHasGrantedPermissionOverride(true);
        });
      }, 300);
    }
  };

  return (
    <RootContainer style={[
      styles.container,
      Platform.OS === 'web' ? { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, overflow: 'hidden' } : {},
      Platform.OS === 'web' && { minHeight: windowHeight }
    ]}>

      {/* --- TOP BAR FIX --- */}
      <View style={styles.topBar} pointerEvents="box-none">
        <View style={{ flexDirection: 'row', gap: 12 }}>
          {isPlayingCustom && !customPuzzleParam && (
            <Pressable
              style={({ hovered }) => [
                styles.iconButton,
                hovered && Platform.OS === 'web' && styles.iconButtonHover
              ]}
              onPress={() => transitionModal('custom')}
            >
              <FontAwesome6 name="arrow-left" size={20} color="#333" solid />
            </Pressable>
          )}

          {(isCompleted) && (
            <Pressable
              style={({ hovered }) => [
                styles.iconButton,
                hovered && Platform.OS === 'web' && styles.iconButtonHover
              ]}
              onPress={() => {
                setTargetVisible(!isTargetVisible);
                setActiveId(null);
              }}
            >
              <FontAwesome6 name={isTargetVisible ? "eye-slash" : "eye"} size={20} color="#333" solid />
            </Pressable>
          )}

          {!isCompleted && !isPlayingCustom && <View style={{ width: 44 }} />}
        </View>

        <Pressable
          style={({ hovered }) => [
            styles.iconButton,
            hovered && Platform.OS === 'web' && styles.iconButtonHover
          ]}
          onPress={() => transitionModal('menu')}
        >
          <FontAwesome6 name="bars" size={24} color="#333333" solid />
        </Pressable>
      </View>

      <ViewShot
        ref={canvasShotRef}
        options={{ format: 'png', quality: 1, result: Platform.OS === 'web' ? 'data-uri' : 'tmpfile' }}
        style={styles.canvasContainer}
        onLayout={(event) => {
          // Trigger a re-render when the container resizes
          const { width, height } = event.nativeEvent.layout;
          // We don't necessarily need to store this in state if we're using refs for export,
          // but the onLayout itself will force a re-render of SvgCanvas which handles its own scaling.
        }}
      >
        {/* 1. Camera Base */}
        <View style={[StyleSheet.absoluteFillObject, { opacity: activeId ? 1 : 0 }]} pointerEvents="none">
          {(shouldMountCamera && (permission?.granted || hasGrantedPermissionOverride)) && (() => {
            // Force the CameraView container to have the exact same aspect ratio as the SVG canvas
            // so that the camera preview's object-fit: cover behavior exactly matches the capture math.
            const svgScale = Math.max(windowWidth / svgDimensions.width, windowHeight / svgDimensions.height);
            const scaledSvgWidth = svgDimensions.width * svgScale;
            const scaledSvgHeight = svgDimensions.height * svgScale;

            return (
              <View style={[
                {
                  position: 'absolute',
                  left: (windowWidth - scaledSvgWidth) / 2,
                  top: (windowHeight - scaledSvgHeight) / 2,
                  width: scaledSvgWidth,
                  height: scaledSvgHeight,
                },
                // Sync the viewfinder scale perfectly with the pixelSampler capture math (* 1.05)
                { transform: [{ scale: 1.05 }] },
                isEasyMode ? (Platform.OS === 'web' ? { filter: 'blur(10px)' } as any : { opacity: 0.5 }) : {}
              ]}>
                <CameraView
                  ref={cameraRef}
                  style={StyleSheet.absoluteFillObject}
                  facing="back"
                  mirror={false}
                />
              </View>
            );
          })()}
        </View>

        <View
          style={[
            StyleSheet.absoluteFillObject,
            {
              backgroundColor: '#fafafa',
              // Hide only while actively sampling from the camera OR viewing target layout
              opacity: (activeId || isTargetVisible) ? 0 : 1,
            }
          ]}
          pointerEvents="none"
        />

        {/* 3. The Captured Layers - UPDATE THIS BLOCK */}
        {(isCompletionAnimating || true) && capturedLayers.map((capture) => {
          // Ensure we have an animation value for this layer
          if (!layerAnims.current.has(capture.id)) {
            layerAnims.current.set(capture.id, new Animated.Value(1));
          }

          const animVal = layerAnims.current.get(capture.id)!;

          // --- THE HIDING LOGIC ---
          // If this layer is currently selected (activeId), we hide its captured image
          // so the live camera feed underneath is visible.
          const isBeingReCaptured = activeId === capture.id;

          return (
            <Animated.View
              key={`img-${capture.id}`}
              style={[
                StyleSheet.absoluteFillObject,
                {
                  opacity: isBeingReCaptured ? 0 : 1,
                  width: '100%',
                  height: '100%',
                  transform: [{ scale: 1.005 }]
                }
              ]}
            >
              {Platform.OS === 'web' ? (
                <img
                  src={(isEasyMode && capture.blurredDisplayUri) ? capture.blurredDisplayUri : (capture.displayUri ?? capture.uri)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center', // <-- The safety net
                    pointerEvents: 'none'
                  }}
                />
              ) : (
                <Image
                  source={{ uri: (isEasyMode && capture.blurredDisplayUri) ? capture.blurredDisplayUri : (capture.displayUri ?? capture.uri) }}
                  style={[StyleSheet.absoluteFillObject, { width: '100%', height: '100%' }]}
                  resizeMode="cover"
                />
              )}
            </Animated.View>
          );
        })}

        {isCompletionAnimating && (
          <View style={[StyleSheet.absoluteFillObject, { userSelect: "none" }]} pointerEvents="none">
            <Svg
              viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
              style={{ width: '100%', height: '100%' }}
              preserveAspectRatio="xMidYMid slice"
            >
              <G>
                {uniqueLayers.map((layer) => {
                  let animVal = layerAnims.current.get(layer.id);
                  if (!animVal) {
                    animVal = new Animated.Value(1);
                    layerAnims.current.set(layer.id, animVal);
                  }
                  return (
                    <AnimatedPath
                      key={`completion-${layer.id}`}
                      d={layer.d}
                      fill={layer.targetHex}
                      opacity={animVal}
                    />
                  );
                })}
              </G>
            </Svg>
          </View>
        )}

        {/* 4. The SVG Template */}
        <Animated.View
          nativeID="svg-canvas"
          id="svg-canvas"
          style={[StyleSheet.absoluteFillObject, { zIndex: 5, elevation: 5, opacity: svgOpacity }]}
          pointerEvents={isCapturing ? 'none' : 'auto'}
        >
          <SvgCanvas
            layers={layers}
            capturedLayerIds={capturedIds}
            activeLayerId={activeId}
            onLayerPress={(id: string) => handleLayerToggle(id, false)}
            isTargetVisible={isTargetVisible}
            isCompletion={isCompletionAnimating}
            svgRef={Platform.OS === 'web' ? svgRef : undefined}
            opacityAnims={layerAnims.current}
            width={svgDimensions.width}
            height={svgDimensions.height}
          />
        </Animated.View>

        {/* Hidden canvas for masked content - only used for export on web */}
        {Platform.OS === 'web' && (
          <canvas
            ref={maskedCanvasRef}
            style={{ display: 'none', position: 'absolute', pointerEvents: 'none' }}
            width={svgDimensions.width * CAPTURE_RESOLUTION_MULTIPLIER}
            height={svgDimensions.height * CAPTURE_RESOLUTION_MULTIPLIER}
          />
        )}
      </ViewShot>

      <View style={styles.controls} pointerEvents="box-none">
        {showTooltip && tutorialAnchor && (
          <View style={styles.tutorialTooltipOverlay} pointerEvents="none">
            <View
              key={`tooltip-${windowWidth}`}
              style={[
                styles.tutorialTooltipContainer,
                { left: tutorialTooltipLeft, top: tutorialTooltipTop, width: tutorialTooltipWidth }
              ]}
            >
              <View
                style={styles.tutorialTooltipBox}
                onLayout={(event) => {
                  const { width, height } = event.nativeEvent.layout;
                  if (!tutorialSize || tutorialSize.width !== width || tutorialSize.height !== height) {
                    setTutorialSize({ width, height });
                  }
                }}
              >
                <Text style={styles.tutorialTooltipText}>👇 Select a layer, find its color, and snap a pic!</Text>
              </View>
              <View style={[styles.tutorialTooltipArrow, { marginLeft: tutorialTooltipArrowLeft }]} />
            </View>
          </View>
        )}

        <Pressable
          style={({ hovered, pressed }) => [
            styles.captureButton,
            hovered && Platform.OS === 'web' && styles.captureButtonHover,
            pressed && !isCapturing && {
              transform: [
                { translateY: 2 },
                { translateX: 2 }
              ],
              opacity: 0.9,
              ...Platform.select({
                web: { boxShadow: `1px 1px 0px ${colors.shadow}` } as any,
                default: { elevation: 2 }
              })
            },
          ]}
          onPress={captureActive}
          disabled={isCapturing}
        >
          <FontAwesome6 name="camera-retro" size={24} color="#ffffff" solid />
        </Pressable>

        {/* Swatch Grid */}
        <View
          key={`swatches-${windowWidth}`}
          style={styles.swatchGrid}
          pointerEvents={isCapturing ? 'none' : 'auto'}
          onLayout={(event) => {
            setSwatchGridLayout(event.nativeEvent.layout);
          }}
        >
          {uniqueLayers.map((layer, index) => {
            const isCaptured = capturedLayers.some(c => c.id === layer.id);
            const isActive = activeId === layer.id;
            const isFirstSwatch = index === 0;

            return (
              <Pressable
                key={`swatch-${layer.id}`}
                onLayout={isFirstSwatch ? (event) => setFirstSwatchLayout(event.nativeEvent.layout) : undefined}
                style={({ hovered, pressed }) => [
                  styles.swatch,
                  { backgroundColor: layer.targetHex },
                  hovered && Platform.OS === 'web' && styles.swatchHover,
                  (pressed || isActive) && styles.activeSwatch,
                ]}
                onPress={() => handleLayerToggle(layer.id)}
              >
                {isCaptured && (
                  <View style={styles.checkBadge}>
                    <FontAwesome6 name="check" size={14} color="#333" solid />
                  </View>
                )}
              </Pressable>
            );
          })}
        </View>
      </View>


      <LandingModal
        visible={activeModal === 'landing'}
        isMuted={isMuted}
        isEasyMode={isEasyMode}
        onPlay={() => handleStartGameplay(startFreshPuzzle)}
        onHowToPlay={() => transitionModal('howToPlay')}
        onBrowsePacks={() => transitionModal('packs')}
        onCreateCustom={() => transitionModal('custom', 'Create a Custom Puzzle')}
        onToggleMute={() => setIsMuted(!isMuted)}
        onToggleEasyMode={() => setIsEasyMode(!isEasyMode)}
        onWhyPiclo={() => transitionModal('whyPiclo')}
      />

      <RestartModal
        visible={activeModal === 'restart'}
        onClose={() => transitionModal(null)}
        onConfirm={() => handleStartGameplay(startFreshPuzzle)}
      />

      <HowToPlayModal
        visible={activeModal === 'howToPlay'}
        onClose={() => transitionModal(null)}
        onBack={howToPlayBackTarget ? () => transitionModal(howToPlayBackTarget) : undefined}
      />

      <MenuModal
        visible={activeModal === 'menu'}
        isMuted={isMuted}
        isEasyMode={isEasyMode}
        isCompleted={isCompleted}
        onClose={() => transitionModal(null)}
        onViewResults={() => {
          transitionModal('completion');
        }}
        onRestart={() => {
          transitionModal('restart');
        }}
        onExportArt={handleExportArt}
        onCreateCustom={() => {
          transitionModal('custom', 'Create a Custom Puzzle');
        }}
        onBrowsePacks={() => transitionModal('packs')}
        onToggleMute={() => setIsMuted(!isMuted)}
        onToggleEasyMode={() => setIsEasyMode(!isEasyMode)}
        onHowToPlay={() => {
          transitionModal('howToPlay');
        }}
        onWhyPiclo={() => transitionModal('whyPiclo')}
        onTermsOfUse={() => transitionModal('termsOfUse')}
        onPrivacyPolicy={() => transitionModal('privacyPolicy')}
        onContact={() => transitionModal('contact')}
      />

      <CompletionModal
        visible={activeModal === 'completion'}
        puzzleNumber={puzzleNumber}
        puzzleTitle={currentPuzzleTitle}
        score={averageScore}
        timeString={timeString}
        streak={streak}
        shareText={shareText}
        layers={uniqueLayers}
        capturedLayers={capturedLayers}
        onExport={handleExportArt}
        onPlayAgain={() => transitionModal('restart')}
        onClose={() => transitionModal(null)}
        onBrowsePacks={() => transitionModal('packs')}
        isCustom={isPlayingCustom}
      />

      <ContinueModal
        visible={activeModal === 'continue'}
        onClose={() => transitionModal(null)}
        onContinue={() => handleStartGameplay(() => transitionModal(null))}
        onRestart={() => handleStartGameplay(startFreshPuzzle)}
      />

      <PackBrowserModal
        visible={activeModal === 'packs'}
        onClose={() => transitionModal(null)}
        onBack={() => transitionModal(packBrowserBackTarget)}
        onSelectPack={(packId, title) => {
          setSelectedPackId(packId);
          const packTitle = title.toLowerCase().includes('pack') ? title : `${title} Pack`;
          setSelectedPackTitle(packTitle);
          transitionModal('puzzles');
        }}
        onCreateCustom={() => transitionModal('custom', 'Create a Custom Puzzle')}
        entitlements={entitlements}
        puzzleCache={puzzleCache}
      />

      <SelectPuzzleModal
        visible={activeModal === 'puzzles'}
        onClose={() => transitionModal(null)}
        onBack={() => transitionModal('packs')}
        onSelectPuzzle={(url, id) => {
          handlePlayCustomPuzzle(url, false, id);
        }}
        onCreateCustom={() => transitionModal('custom', 'Create a Custom Puzzle')}
        packTitle={selectedPackTitle}
        packId={selectedPackId}
        hasPicloPlay={entitlements.picloPlay}
        isPackOwned={entitlements.picloPlay || entitlements.unlockedPacks.includes(selectedPackId || '')}
        priceLabel={selectedPackId === 'pack-free' || selectedPackId === 'animals' ? 'Free' : '$3'}
        packPriceId={mockPacks.find(p => p.id === selectedPackId)?.priceId}
        onUnlockFreePack={(packId) => {
          setEntitlements(prev => {
            const updated = {
              ...prev,
              unlockedPacks: Array.from(new Set([...prev.unlockedPacks, packId]))
            };
            AsyncStorage.setItem('@piclo:entitlements', JSON.stringify(updated))
              .catch(err => console.error("Failed to save entitlement", err));
            return updated;
          });
          const pack = mockPacks.find(p => p.id === packId);
          if (pack) setUnlockedPackName(pack.title);
          setIsUnlockedPicloPlay(false);
          setShowUnlockedModal(true);
          AsyncStorage.setItem('@piclo:showUnlockedSuccess', JSON.stringify({ packName: pack?.title }))
            .catch(err => console.error("Failed to save success flag", err));
        }}
        puzzleCache={puzzleCache}
      />

      <CustomModelModal
        visible={activeModal === 'custom'}
        onClose={() => transitionModal(null)}
        onBack={customBackTarget ? () => transitionModal(customBackTarget) : undefined}
        hasPicloPlay={entitlements.picloPlay}
        title={customModalTitle}
        onPlayPuzzle={handlePlayCustomPuzzle}
        onSimulatePurchase={() => setEntitlements(prev => ({ ...prev, picloPlay: true }))}
        customPuzzleCount={customPuzzleCount}
        onRefreshEntitlements={refreshCustomEntitlements}
        isLoading={isCustomLoading}
      />

      <WhyPicloModal
        visible={activeModal === 'whyPiclo'}
        onClose={() => transitionModal('landing')}
      />

      <TermsOfUseModal
        visible={activeModal === 'termsOfUse'}
        onBack={() => transitionModal('menu')}
        onClose={() => transitionModal(null)}
      />

      <PrivacyPolicyModal
        visible={activeModal === 'privacyPolicy'}
        onBack={() => transitionModal('menu')}
        onClose={() => transitionModal(null)}
      />

      <CameraPermissionModal
        visible={!!showPermissionModal}
        onTryAgain={async () => {
          setIsRequestingPermission(true);
          const res = await requestPermission();
          setIsRequestingPermission(false);
          if (res?.granted) setHasGrantedPermissionOverride(true);
        }}
      />

      <UnlockedModal
        visible={showUnlockedModal}
        packName={unlockedPackName || undefined}
        isPicloPlay={isUnlockedPicloPlay}
        onClose={() => {
          setShowUnlockedModal(false);
          AsyncStorage.removeItem('@piclo:showUnlockedSuccess');

          if (isUnlockedPicloPlay) {
            transitionModal('packs');
          } else if (unlockedPackName) {
            const pack = mockPacks.find(p => p.title.toLowerCase() === unlockedPackName.toLowerCase());
            if (pack) {
              setSelectedPackId(pack.id);
              const packTitle = pack.title.toLowerCase().includes('pack') ? pack.title : `${pack.title} Pack`;
              setSelectedPackTitle(packTitle);
              transitionModal('puzzles');
            } else {
              transitionModal('packs');
            }
          } else {
            transitionModal('packs');
          }

          setUnlockedPackName(null);
          setIsUnlockedPicloPlay(false);
        }}
      />

      <CustomPuzzleGreetingModal
        visible={activeModal === 'customGreeting'}
        onClose={() => {
          void loadDailyPuzzle();
          transitionModal('landing');
        }}
        isLoading={isCustomLoading}
        onPlay={() => {
          handleStartGameplay(startFreshPuzzle);
        }}
      />

      <ExpiredPuzzleModal
        visible={activeModal === 'expiredCustom'}
        onClose={() => {
          // 1. Clear the temporary ID
          setPendingCustomId(null);

          // 2. Strip the URL parameter so the app knows it's truly home
          router.replace('/');

          // 3. Show the main menu
          setActiveModal('landing');
        }}
      />

      {isAuthenticating && (
        <View style={[StyleSheet.absoluteFill, {
          backgroundColor: 'rgba(0,0,0,0.85)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999
        }]}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={{
            ...typography.header,
            color: '#fff',
            marginTop: 16,
            textAlign: 'center',
            fontFamily: 'JosefinSans_600SemiBold'
          }}>
            Authenticating Purchase...
          </Text>
          <Text style={{
            ...typography.body,
            color: colors.textMuted,
            marginTop: 8,
            textAlign: 'center',
            paddingHorizontal: 32
          }}>
            We are securing your premium access. This will only take a moment!
          </Text>
        </View>
      )}
    </RootContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  topBar: {
    position: 'absolute',
    top: 20,
    left: 20,
    right: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    zIndex: 20,
    elevation: 20,
  },
  activeSwatch: {
    borderWidth: 3,
    borderColor: colors.secondary,
    transform: [
      { translateY: -2 },
      { scale: 1.1 }
    ],
    ...Platform.select({
      web: {
        boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      } as any,
      default: {
        shadowColor: colors.secondary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 8,
      }
    })
  },
  swatchHover: {
    transform: [{ translateY: -2 }],
    ...Platform.select({
      web: {
      } as any,
      default: {
        shadowOffset: { width: 4, height: 4 },
        elevation: 4,
      }
    })
  },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 4,
    borderColor: 'rgba(255, 255, 255, 0.9)',
    ...Platform.select({
      web: {
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.25), 0 1px 4px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.25,
        shadowRadius: 5,
        elevation: 5,
      }
    })
  },
  iconButtonHover: {
    transform: [{ translateY: -2 }],
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      } as any,
      default: {
        elevation: 8,
      }
    })
  },
  iconText: {
    fontSize: 20,
  },
  tutorialTooltipOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 999,
    pointerEvents: 'none',
  },
  tutorialTooltipContainer: {
    position: 'absolute',
    alignItems: 'flex-start',
  },
  tutorialTooltipBox: {
    backgroundColor: '#333333',
    paddingTop: 8,
    paddingBottom: 6,
    paddingLeft: 6,
    paddingRight: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 15,
        elevation: 6,
      }
    }),
  },
  tutorialTooltipText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 22,
    fontFamily: 'JosefinSans_600SemiBold',
    padding: 5,
    textAlign: 'left',
  },
  tutorialTooltipArrow: {
    marginTop: -1,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#333',
  },
  canvasContainer: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
  },
  checkBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.90)',
    borderRadius: 4,
    padding: 2,
    paddingLeft: 4,
    paddingRight: 4,
    ...Platform.select({
      web: {
        boxShadow: '2px 2px 0px rgba(0,0,0,0.1)',
      } as any,
      default: {
        shadowColor: 'rgba(0,0,0,0.1)',
        shadowOffset: { width: 2, height: 2 },
        shadowOpacity: 1,
        shadowRadius: 0,
        elevation: 3,
      }
    })
  },
  controls: {
    position: 'absolute',
    bottom: 15,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 10,
    overflow: 'visible',
  },
  swatchGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    columnGap: 0,
    rowGap: 10,
    width: '100%',
    paddingHorizontal: 5,
  },
  swatch: {
    width: 54,
    height: 54,
    marginHorizontal: 8,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    borderColor: 'rgba(255, 255, 255, 0.9)',
    ...Platform.select({
      web: {
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.25), 0 1px 4px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.25,
        shadowRadius: 5,
        elevation: 5,
      }
    })
  },
  captureButton: {
    width: 60,
    height: 60,
    borderRadius: 32,
    backgroundColor: colors.secondary,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 4,
    borderColor: 'rgba(255, 255, 255, 0.9)',
    ...Platform.select({
      web: {
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.25), 0 1px 4px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.25,
        shadowRadius: 5,
        elevation: 6,
      }
    }),
    marginBottom: 16, // Approximately 1.5rem
  },
  captureButtonHover: {
    transform: [{ translateY: -2 }],
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      } as any,
      default: {
        elevation: 10,
      }
    })
  },
  captureButtonDisabled: {
    backgroundColor: '#A0A0A0',
    borderColor: '#ddd',
  },
  permissionButton: {
    backgroundColor: '#333',
    padding: 16,
    borderRadius: 30,
    alignSelf: 'center',
    marginTop: 20,
  }
});
